import { LogFirebase } from "../services/firestore";
import { GoogleGenAI } from "@google/genai";
import { GEMINI_API_KEY, GEMINI_MODEL } from "../constants/AskAIConstants";
import askAIApi from "../apis/askAIApi";
import { extractBase64Data } from "./validateImage";

/**
 * Smart Actions Generator - FULLY DYNAMIC FROM GEMINI
 *
 * All actions are generated by Gemini based on image analysis.
 * NO static prompts, NO predefined actions, NO hardcoded categories.
 *
 * Conversation history is passed for context-aware suggestions.
 */

/**
 * Generate text response AND new smart actions
 *
 * TWO MODES:
 * 1. Initial Analysis (selectedActionPrompt is empty/null): Send only image, rely on system instruction
 * 2. Follow-up Action (selectedActionPrompt provided): Send image + specific action prompt
 *
 * @param {string} imageBase64 - Base64 encoded image
 * @param {string} selectedActionPrompt - The prompt text (empty for initial analysis, specific text for follow-ups)
 * @param {Array} conversationHistory - Previous messages for context (snapshot-specific)
 * @returns {Promise<Object>} - { textResponse: string, actions: Array, suggestNewSnapshot: boolean }
 */
export async function generateResponseWithActions(imageBase64, selectedActionPrompt, conversationHistory = []) {
    try {
        const isInitialAnalysis = !selectedActionPrompt || selectedActionPrompt.trim() === "";
        LogFirebase("SmartActions", "generateResponseWithActions_started", { isInitialAnalysis });

        if (!imageBase64) {
            LogFirebase("SmartActions", "generateResponseWithActions_missing_image", "");
            return {
                textResponse: "Unable to process request. Please try again.",
                actions: await generateFallbackActions(imageBase64),
                suggestNewSnapshot: false,
            };
        }

        // Validate image data before processing
        const imageValidation = extractBase64Data(imageBase64);
        if (!imageValidation.success) {
            LogFirebase("SmartActions", "generateResponseWithActions_invalid_image", imageValidation.error);
            return {
                textResponse: `Image validation failed: ${imageValidation.error}`,
                actions: [],
                suggestNewSnapshot: true,
            };
        }

        const ai = new GoogleGenAI({
            apiKey: GEMINI_API_KEY,
        });

        // MODE 1: Initial Analysis - Use system instruction only (no explicit prompt)
        // SHORTER responses: 50-80 words max, concise and actionable
        const PROMPT_INITIAL_ANALYSIS = `Analyze this image and provide BRIEF troubleshooting guidance with 5 Smart Actions.

=== RESPONSE REQUIREMENTS ===
- Keep response VERY SHORT: 50-80 words maximum
- Use bullet points for key observations only
- No lengthy explanations or introductions
- Focus on actionable insights only
- Include extracted data (serial/model numbers) if visible

=== OUTPUT FORMAT ===

AI_RESPONSE_START
[Your BRIEF analysis in 50-80 words max using markdown bullet points]
AI_RESPONSE_END

ACTIONS_START
[
{
    "label": "Action 1",
    "prompt": "Detailed instruction for action 1",
    "reason": "Why this action is suggested"
},
{
    "label": "Action 2",
    "prompt": "Detailed instruction for action 2",
    "reason": "Why this action is suggested"
},
{
    "label": "Action 3",
    "prompt": "Detailed instruction for action 3",
    "reason": "Why this action is suggested"
},
{
    "label": "Action 4",
    "prompt": "Detailed instruction for action 4",
    "reason": "Why this action is suggested"
},
{
    "label": "Action 5",
    "prompt": "Detailed instruction for action 5",
    "reason": "Why this action is suggested"
}
]
ACTIONS_END

Output ONLY the two sections above. No additional text.`;

        // MODE 2: Follow-up Action - SHORTER responses (60-100 words max)
        const PROMPT_SMART_ACTION_FOLLOWUP = `You are a technical support AI providing BRIEF follow-up analysis after a Smart Action was selected.

=== RESPONSE REQUIREMENTS ===
- Keep response SHORT: 60-100 words maximum
- Use bullet points for findings
- No verbose explanations or introductions
- Direct answer to the action requested
- Include only key technical details

DO NOT:
✗ Re-describe the entire image
✗ Re-extract data already captured
✗ Use generic intros
✗ Write lengthy paragraphs

=== OUTPUT FORMAT ===

AI_RESPONSE_START
[Your BRIEF 60-100 word response using markdown bullet points]
AI_RESPONSE_END

ACTIONS_START
[
{
    "label": "New Action 1",
    "prompt": "Detailed instruction for new action 1",
    "reason": "Why this action based on current findings"
},
{
    "label": "New Action 2",
    "prompt": "Detailed instruction for new action 2",
    "reason": "Why this action based on current findings"
},
{
    "label": "New Action 3",
    "prompt": "Detailed instruction for new action 3",
    "reason": "Why this action based on current findings"
},
{
    "label": "New Action 4",
    "prompt": "Detailed instruction for new action 4",
    "reason": "Why this action based on current findings"
},
{
    "label": "New Action 5",
    "prompt": "Detailed instruction for new action 5",
    "reason": "Why this action based on current findings"
}
]
ACTIONS_END

Output ONLY the two sections above. No additional text.`;

        // Build prompt based on mode
        let combinedPrompt;
        if (isInitialAnalysis) {
            // MODE 1: Initial analysis - simple prompt, rely on system instruction
            combinedPrompt = PROMPT_INITIAL_ANALYSIS;
        } else {
            // MODE 2: Follow-up action - detailed prompt with selected action context
            combinedPrompt = `${PROMPT_SMART_ACTION_FOLLOWUP}\n\n=== SELECTED SMART ACTION ===\nThe user clicked this Smart Action button:\n"${selectedActionPrompt}"\n\nProvide a detailed response (100-200 words) specifically addressing this action, then generate 5 NEW follow-up Smart Actions.`;
        }

        const contents = formatConversationHistory(conversationHistory, imageBase64, combinedPrompt);
        const config = askAIApi.buildConfig();
        const response = await ai.models.generateContent({
            model: GEMINI_MODEL,
            contents: contents,
            config: config,
        });

        const responseText = response?.text || "";
        LogFirebase("SmartActions", "generateResponseWithActions_response_received", responseText.substring(0, 100));

        const parsed = parseResponseWithActions(responseText);

        // Detect if Gemini suggests taking a new photo
        const suggestNewSnapshot = detectNewSnapshotSuggestion(parsed.textResponse, parsed.actions);

        // Ensure minimum 5 actions
        if (parsed.actions.length < 5) {
            const fallbackActions = await generateFallbackActions(imageBase64);
            parsed.actions = [...parsed.actions, ...fallbackActions].slice(0, 6);
        }

        LogFirebase("SmartActions", "generateResponseWithActions_success", {
            textLength: parsed.textResponse.length,
            actionCount: parsed.actions.length,
            suggestNewSnapshot: suggestNewSnapshot,
        });

        return {
            textResponse: parsed.textResponse,
            actions: parsed.actions,
            suggestNewSnapshot: suggestNewSnapshot,
        };
    } catch (error) {
        console.error("Error generating response with actions:", error);
        LogFirebase("SmartActions", "generateResponseWithActions_error", error?.message);
        return {
            textResponse: "I encountered an error processing your request. Please try again or try a different action.",
            actions: await generateFallbackActions(imageBase64),
            suggestNewSnapshot: false,
        };
    }
}

/**
 * Format conversation history into Gemini contents array
 * Only includes history for the current snapshot (not previous snapshots)
 */
function formatConversationHistory(conversationHistory, currentImage, currentPrompt) {
    const contents = [];

    // Add previous conversation messages (only if they exist and have valid data)
    if (conversationHistory && conversationHistory.length > 0) {
        conversationHistory.forEach((message) => {
            const parts = [];

            // Add image if present (with validation)
            if (message.image) {
                const extraction = extractBase64Data(message.image);
                if (extraction.success) {
                    parts.push({
                        inlineData: {
                            mimeType: extraction.mimeType || "image/jpeg",
                            data: extraction.data,
                        },
                    });
                } else {
                    console.error("Invalid image in message history:", extraction.error);
                }
            }

            // Add text if present
            if (message.text) {
                parts.push({
                    text: message.text,
                });
            }

            // Only add if we have valid parts
            if (parts.length > 0) {
                contents.push({
                    role: message.type === "USER" ? "user" : "model",
                    parts: parts,
                });
            }
        });
    }

    // Add current request with image and prompt (with validation)
    const currentExtraction = extractBase64Data(currentImage);
    if (currentExtraction.success) {
        contents.push({
            role: "user",
            parts: [
                {
                    inlineData: {
                        mimeType: currentExtraction.mimeType || "image/jpeg",
                        data: currentExtraction.data,
                    },
                },
                {
                    text: currentPrompt,
                },
            ],
        });
    } else {
        console.error("Error validating current image:", currentExtraction.error);
        // Fallback: just add text prompt
        contents.push({
            role: "user",
            parts: [
                {
                    text: currentPrompt,
                },
            ],
        });
    }

    return contents;
}

/**
 * Returns default fallback actions when image validation fails or no image is provided
 * @returns {Array} - Default action buttons
 */
function getDefaultFallbackActions() {
    return [
        {
            id: "fallback-1",
            label: "Request Closer View",
            prompt: "Can you move the camera closer to get a clearer view of the device or component?",
            reason: "Better visibility will help with analysis",
            confidence: 0.5,
            category: "clarification",
        },
        {
            id: "fallback-2",
            label: "Improve Lighting",
            prompt: "Can you improve the lighting or move to a brighter area?",
            reason: "Better lighting is needed for accurate identification",
            confidence: 0.5,
            category: "clarification",
        },
        {
            id: "fallback-3",
            label: "Show Labels/Serial",
            prompt: "Can you show any labels, serial numbers, or model information visible on the device?",
            reason: "Identification details will help determine next steps",
            confidence: 0.6,
            category: "identification",
        },
        {
            id: "fallback-4",
            label: "Different Angle",
            prompt: "Can you capture the image from a different angle or perspective?",
            reason: "An alternative view may reveal important details",
            confidence: 0.5,
            category: "clarification",
        },
        {
            id: "fallback-5",
            label: "Describe Issue",
            prompt: "Can you describe what problem or issue you're experiencing with this device?",
            reason: "Understanding the issue will guide troubleshooting",
            confidence: 0.6,
            category: "clarification",
        },
    ];
}

/**
 * Generate fallback actions using Gemini's vision labels (not static list)
 * Only used when Gemini completely fails
 */
async function generateFallbackActions(imageBase64) {
    try {
        // Validate image before fallback processing
        if (!imageBase64) {
            return getDefaultFallbackActions();
        }

        const extraction = extractBase64Data(imageBase64);
        if (!extraction.success) {
            console.error("Fallback: Invalid image data:", extraction.error);
            return getDefaultFallbackActions();
        }

        // Try to get basic vision analysis from Gemini
        const ai = new GoogleGenAI({ apiKey: GEMINI_API_KEY });
        const base64Data = extraction.data;

        const fallbackPrompt = `Describe what you see in 1 sentence. Then suggest 5 basic troubleshooting actions.

Return JSON:
[
  {"label": "Action 1", "prompt": "Instruction", "reason": "Why"},
  {"label": "Action 2", "prompt": "Instruction", "reason": "Why"},
  {"label": "Action 3", "prompt": "Instruction", "reason": "Why"},
  {"label": "Action 4", "prompt": "Instruction", "reason": "Why"},
  {"label": "Action 5", "prompt": "Instruction", "reason": "Why"}
]`;

        const response = await ai.models.generateContent({
            model: GEMINI_MODEL,
            contents: [
                {
                    role: "user",
                    parts: [
                        {
                            inlineData: {
                                mimeType: "image/jpeg",
                                data: base64Data,
                            },
                        },
                        {
                            text: fallbackPrompt,
                        },
                    ],
                },
            ],
            generationConfig: {
                temperature: 0.5,
                maxOutputTokens: 600,
            },
        });

        const actions = parseActionsFromResponse(response?.text || "");
        if (actions.length >= 5) {
            return actions;
        }
    } catch (error) {
        console.error("Fallback generation failed:", error);
    }

    // Last resort: generic clarification actions
    return getDefaultFallbackActions();
}

/**
 * Detect if response suggests capturing a new photo
 */
function detectNewSnapshotSuggestion(textResponse, actions) {
    const textLower = textResponse.toLowerCase();
    const suggestionKeywords = [
        "take a photo",
        "capture",
        "new image",
        "updated photo",
        "move closer",
        "better lighting",
        "different angle",
        "clearer view",
        "show me",
    ];

    // Check text response
    const textSuggests = suggestionKeywords.some((keyword) => textLower.includes(keyword));

    // Check action labels
    const actionsSuggest = actions.some((action) => {
        const labelLower = action.label.toLowerCase();
        const promptLower = action.prompt.toLowerCase();
        return suggestionKeywords.some((keyword) => labelLower.includes(keyword) || promptLower.includes(keyword));
    });

    return textSuggests || actionsSuggest;
}

/**
 * Parse JSON actions from Gemini response
 */
function parseActionsFromResponse(responseText) {
    try {
        let cleaned = responseText.trim();
        cleaned = cleaned.replace(/```json\s*/gi, "").replace(/```\s*/g, "");

        const jsonMatch = cleaned.match(/\[[\s\S]*\]/);
        if (!jsonMatch) {
            console.warn("No JSON array found in response");
            return [];
        }

        const parsed = JSON.parse(jsonMatch[0]);

        if (!Array.isArray(parsed)) {
            console.warn("Parsed result is not an array");
            return [];
        }

        const validActions = parsed
            .filter((action) => action.label && action.prompt)
            .map((action, index) => ({
                id: `gemini-action-${Date.now()}-${index}`,
                label: (action.label || "Action").substring(0, 30),
                prompt: action.prompt || "Please analyze the image.",
                reason: action.reason || "Suggested action based on image",
                confidence: 0.9,
                category: "gemini-dynamic",
            }));

        return validActions;
    } catch (error) {
        console.error("Error parsing actions from response:", error);
        return [];
    }
}

/**
 * Parse structured response containing both text and actions
 * Handles multiple marker formats for robustness
 */
function parseResponseWithActions(responseText) {
    try {
        let textResponse = "";
        let actions = [];

        // Try primary format: AI_RESPONSE_START ... AI_RESPONSE_END
        let textMatch = responseText.match(/AI_RESPONSE_START\s*([\s\S]*?)\s*AI_RESPONSE_END/i);

        // Fallback: Try alternate formats with asterisks or other variations
        if (!textMatch) {
            textMatch = responseText.match(/AI[_*]RESPONSE[_*]START\s*([\s\S]*?)\s*AI[_*]RESPONSE[_*]END/i);
        }

        // If we found a match, extract the text
        if (textMatch) {
            textResponse = textMatch[1].trim();
        } else {
            // No markers found - try to extract text before ACTIONS_START
            const beforeActions = responseText.split(/ACTIONS[_*]START/i)[0];
            if (beforeActions) {
                // Clean up any partial markers at the end
                textResponse = beforeActions.replace(/AI[_*]RESPONSE[_*](START|END)/gi, "").trim();
            } else {
                textResponse = responseText;
            }
        }

        // Clean up any remaining marker fragments in the text response
        textResponse = textResponse
            .replace(/AI[_*]RESPONSE[_*](START|END)/gi, "")
            .replace(/^[_*\s]+|[_*\s]+$/g, "")
            .trim();

        // Try to extract actions
        const actionsMatch = responseText.match(/ACTIONS_START\s*([\s\S]*?)\s*ACTIONS_END/i);

        if (actionsMatch) {
            const actionsText = actionsMatch[1].trim();
            actions = parseActionsFromResponse(actionsText);
        }

        // Fallback: try to parse entire response if no actions found
        if (actions.length === 0) {
            actions = parseActionsFromResponse(responseText);
        }

        return {
            textResponse: textResponse || "Analysis complete.",
            actions: actions,
        };
    } catch (error) {
        console.error("Error parsing response with actions:", error);
        // Still try to clean up the response text
        const cleanedText = responseText
            .replace(/AI[_*]RESPONSE[_*](START|END)/gi, "")
            .replace(/ACTIONS[_*](START|END)/gi, "")
            .trim();

        return {
            textResponse: cleanedText || "Unable to parse response.",
            actions: [],
        };
    }
}
